import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Loader2, Send } from 'lucide-react';
import { apiGet, apiPost, apiPut } from '../../lib/api.js';
import { EMAIL_TEMPLATES as TEMPLATES } from '../../lib/emailTemplates.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Quick in-call email to the lead; the call stays connected.
 * @param {Object} props
 * @param {string|null} props.leadId
 * @param {string} [props.leadEmail] - Email from the dial lookup, if known.
 * @param {() => void} props.onSent - Called after a successful send (collapse the form).
 * @returns {JSX.Element}
 */
export default function EmailComposer({ leadId, leadEmail = '', onSent }) {
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  // The dial lookup may not carry the email (calls started elsewhere): fetch the lead.
  const { data } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => apiGet(`/leads/${leadId}`),
    enabled: !!leadId && !leadEmail,
    staleTime: 30_000,
  });
  const onFile = leadEmail || data?.lead?.email || '';
  const to = onFile || email.trim();

  function applyTemplate(id) {
    const t = TEMPLATES.find((x) => x.id === id);
    if (t) { setSubject(t.subject); setBody(t.body); }
  }

  async function send() {
    if (!leadId) { toast.error('This call is not linked to a lead'); return; }
    if (!EMAIL_RE.test(to)) { toast.error('Enter a valid email address'); return; }
    if (!subject.trim() || !body.trim()) { toast.error('Add a subject and a message'); return; }
    setSending(true);
    try {
      // The send endpoint reads the lead's stored email, so save a newly typed one first.
      if (!onFile) await apiPut(`/leads/${leadId}`, { email: to });
      await apiPost(`/leads/${leadId}/email`, { subject: subject.trim(), body });
      toast.success('Email sent to lead');
      setEmail(''); setSubject(''); setBody('');
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not send email');
    } finally {
      setSending(false);
    }
  }

  const field = 'w-full bg-civix-panel border border-civix-border rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/30 focus:outline-hidden focus:border-civix-purple';

  return (
    <div className="rounded-xl border border-civix-border bg-civix-panelLight/60 p-3 space-y-2" data-testid="email-composer">
      {!leadId && <p className="text-[11px] text-amber-300/80">Link this call to a lead to send email (open it from the Leads page).</p>}

      <div>
        <label className="text-[10px] uppercase tracking-wider text-white/40">To</label>
        {onFile ? (
          <div className="text-xs text-white/80 mt-0.5 truncate">{onFile}</div>
        ) : (
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="lead@email.com (not on file)" className={`${field} mt-0.5`} type="email" />
        )}
      </div>

      <select className={field} value="" onChange={(e) => applyTemplate(e.target.value)} aria-label="Quick template">
        <option value="">Insert a template…</option>
        {TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>

      <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Following up on our call" className={field} />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Write your message…" className={`${field} resize-none`} />

      <button
        onClick={send}
        disabled={sending || !leadId}
        className="w-full py-2.5 rounded-lg bg-civix-purple hover:bg-civix-purpleDeep disabled:opacity-50 text-white text-xs font-bold flex items-center justify-center gap-2 transition"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Send Email
      </button>
    </div>
  );
}
